import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/pod/api')

/** 回单类型：运单签满时服务端自动补一条 electronic；paper 留给纸单补录 */
export type ReceiptKind = 'electronic' | 'paper'

/** 回单状态：pending → submitted → returned →（可再次提交）submitted */
export type ReceiptStatus = 'pending' | 'submitted' | 'returned'

/** 可触发的回单动作，与后端 POST /receipts/:id/<action> 一一对应 */
export type ReceiptAction = 'submit' | 'return'

/** 一张回单。字段与 services/pod/internal/receipt/model.go 的 Receipt 一一对应。 */
export interface Receipt {
  id: number
  tenant_id: number
  shipment_id: number
  /** 回单号，前缀 HD */
  receipt_no: string
  kind: ReceiptKind
  status: ReceiptStatus
  submitted_at: string | null
  returned_at: string | null
  remark: string
  created_at: string
  updated_at: string
}

/** 列表行：回单 + 所属运单单号（后端子查询带出，不 JOIN） */
export interface ReceiptRow extends Receipt {
  shipment_doc_no: string
}

/** 动作入参：只有可选备注（后端 bindOptional 放行空 body） */
export interface ReceiptActionInput {
  remark?: string
}

export interface ReceiptQuery extends PageQuery {
  /** 回单号或所属运单号模糊匹配 */
  keyword?: string
  status?: string
  kind?: string
}

/** 与后端 services/pod/internal/receipt/model.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<ReceiptStatus, string> = {
  pending: '待提交',
  submitted: '已提交',
  returned: '已退回',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<ReceiptStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> =
  {
    pending: 'warning',
    submitted: 'success',
    returned: 'danger',
  }

/** 与后端 services/pod/internal/receipt/model.go 的 KindNames 同源两份 */
export const KIND_LABELS: Record<ReceiptKind, string> = {
  electronic: '电子回单',
  paper: '纸质回单',
}

/**
 * 各状态允许的动作，抄后端 internal/receipt/service.go：
 * Submit 挡的是「已经提交」（pending/returned 都能提交），Return 只允许 submitted。
 */
const ACTIONS: Record<ReceiptStatus, ReceiptAction[]> = {
  pending: ['submit'],
  submitted: ['return'],
  returned: ['submit'],
}

/** 状态能不能触发某个动作（最终以后端 409 为准） */
export function canAct(status: ReceiptStatus, action: ReceiptAction): boolean {
  return ACTIONS[status].includes(action)
}

export const listReceipts = (q: ReceiptQuery): Promise<PageResult<ReceiptRow>> =>
  api.get<PageResult<ReceiptRow>>('/receipts', { ...q })

export const getReceipt = (id: number): Promise<ReceiptRow> =>
  api.get<ReceiptRow>(`/receipts/${id}`)

const act = (id: number, action: ReceiptAction, input?: ReceiptActionInput): Promise<Receipt> =>
  api.post<Receipt>(`/receipts/${id}/${action}`, input ?? {})

export const submitReceipt = (id: number, input?: ReceiptActionInput): Promise<Receipt> =>
  act(id, 'submit', input)

export const returnReceipt = (id: number, input?: ReceiptActionInput): Promise<Receipt> =>
  act(id, 'return', input)
